import React from 'react'
import Bugsnag, { type Event } from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'

// In Next.js 14, publicRuntimeConfig is removed. Use environment variables directly.
const BUGSNAG_API_KEY = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || 'e16a3cbf3b3599bb4fa39db9cfeda230'
const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV
const isServer = typeof window === 'undefined'
const enabledReleaseStages = ['production', 'staging']
const releaseStage = APP_ENV || process.env.NODE_ENV || 'development'
const appType = isServer ? 'web_server' : 'client'
const shouldEnableBugsnag = enabledReleaseStages.includes(releaseStage)

// Fehlerfilter: siehe openspec/specs/bugsnag-error-filtering
const THIRD_PARTY_HOSTS = [
  'aticdn.net',
  'glomex.com',
  'imasdk.googleapis.com',
  'cdntrf.com',
  'outbrain.com',
  'consentmanager.net',
]
const AD_CREATIVE_METHODS = ['Placement', 'TrackingCenter', 'CrossScreenBannerFormat']
const SKIPPED_FILE_PARTS = ['fundingchoicesmessages', 'mraid', 'content-ads']
const NETWORK_ERROR_MESSAGES = ['Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch resource.']
const SKIPPED_MESSAGE_PARTS = [
  'this._resizeCallback',
  'this.getContainer().ownerDocument',
  "(reading 'ownerDocument')",
  'pbjs',
  '.style',
  "(reading 'style')",
  "Unexpected token ':'. Expected ')' to end an argument list.",
]

function startBugsnag() {
  if (!shouldEnableBugsnag) {
    return null
  }

  if (Bugsnag.isStarted()) {
    return Bugsnag
  }

  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    ...(isServer ? {} : { plugins: [new BugsnagPluginReact() as any] }),
    enabledReleaseStages,
    releaseStage,
    appType,
    autoTrackSessions: false,
    collectUserIp: false,
    generateAnonymousId: false,
    onError: (event) => {
      if (shouldSkipError(event)) {
        return false;
      }

      if (event?.errors) {
        event.addMetadata('custom_errors', {
          errors: event.errors,
        });
        event.addMetadata('custom_error_version', {
          version: 1,
          shouldSkipError: shouldSkipError(event),
        });
      }

      return true;
    },
  })

  return Bugsnag
}

startBugsnag()

function ensureBugsnagStarted() {
  if (!shouldEnableBugsnag) {
    return null
  }

  if (!Bugsnag.isStarted()) {
    return startBugsnag()
  }

  return Bugsnag
}

const FallbackErrorBoundary = ({ children }: { children: React.ReactNode }) =>
  React.createElement(React.Fragment, null, children)

function getBugsnagErrorBoundary() {
  if (isServer) {
    return null
  }

  const bugsnagClient = ensureBugsnagStarted()
  if (!bugsnagClient) {
    return null
  }

  const bugsnagPlugin = bugsnagClient.getPlugin('react')
  return bugsnagPlugin?.createErrorBoundary(React) || null
}

export const ErrorBoundary = ({ children, ...props }: React.PropsWithChildren<Record<string, unknown>>) => {
  const BugsnagErrorBoundary = getBugsnagErrorBoundary()

  if (!BugsnagErrorBoundary) {
    return React.createElement(FallbackErrorBoundary, null, children)
  }

  return React.createElement(BugsnagErrorBoundary, props, children)
}

type BugsnagOnError = (event: Event) => void

export function logBugsnagError(error: unknown, onError?: BugsnagOnError) {
  const bugsnagClient = ensureBugsnagStarted()
  if (!bugsnagClient) {
    return
  }

  const payload = typeof error === 'string' ? new Error(error) : error
  bugsnagClient.notify(payload as Error, (event) => {
    if (onError) {
      onError(event)
    }
  })
}

type FilterableFrame = { file?: string; method?: string }
type FilterableError = { errorMessage?: string; stacktrace?: FilterableFrame[] }

function isThirdPartyFile(file?: string) {
  if (!file) {
    return false
  }
  try {
    const { hostname } = new URL(file)
    return THIRD_PARTY_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
  } catch {
    return false
  }
}

export function shouldSkipErrors(errors?: FilterableError[]): boolean {
  for (const error of errors ?? []) {
    const frames = error?.stacktrace ?? []

    if (frames.some((frame) =>
      SKIPPED_FILE_PARTS.some((part) => frame?.file?.includes(part)) ||
      AD_CREATIVE_METHODS.some((method) => frame?.method?.includes(method)))) {
      return true
    }

    // Auslösestelle = oberster Frame mit echter Datei
    const origin = frames.find((frame) => frame?.file && frame.file !== '[native code]')
    if (isThirdPartyFile(origin?.file)) {
      return true
    }

    // Bei Netzwerkfehlern liegt Bugsnags fetch-Wrapper (unser Bundle) oben
    if (NETWORK_ERROR_MESSAGES.includes(error?.errorMessage ?? '') &&
        frames.some((frame) => isThirdPartyFile(frame?.file))) {
      return true
    }
  }

  const errorMessage = errors?.[0]?.errorMessage
  if (errorMessage) {
    if (errorMessage.startsWith('ResizeObserver loop') ||
        SKIPPED_MESSAGE_PARTS.some((part) => errorMessage.includes(part))) {
      return true
    }
  }

  return false
}

function shouldSkipError(event) {
  try {
    return shouldSkipErrors(event?.errors)
  } catch (error) {
    console.log(error);
    return false
  }
}

export default Bugsnag
